// =============================================================================
// Diary lock — a numeric code and/or Face ID in front of the diary page.
//
// Setting (data.settings.diaryLock, synced via user_settings.diary_lock):
//   { enabled: boolean, codeHash: string|null, salt: string|null, biometrics: boolean }
//
// The code itself is never stored, only SHA-256(salt + code) as hex. This is
// a privacy curtain against someone holding the unlocked phone, not
// encryption: the entries stay readable in the database and the local cache,
// and the lock can be switched off in Settings.
//
// Two halves:
//   * pure helpers — hashCode / newSalt / verifyCode / isValidCode;
//   * session state — isUnlocked / markUnlocked / lock. Lives in module
//     memory (never localStorage) so a reload always starts locked; an unlock
//     ends when the page is hidden, the native app goes to the background,
//     or after UNLOCK_TTL_MS, whichever comes first.
//
// Face ID / Touch ID goes through the EuleaWidgetBridge Capacitor plugin
// (`authenticate({ reason })` → `{ ok, available }`), native builds only.
// =============================================================================
import { Capacitor, registerPlugin } from '@capacitor/core';

export const MIN_CODE_LENGTH = 4;
export const MAX_CODE_LENGTH = 8;
export const UNLOCK_TTL_MS = 5 * 60 * 1000;

export const DEFAULT_DIARY_LOCK = Object.freeze({
  enabled: false,
  codeHash: null,
  salt: null,
  biometrics: false
});

// Tolerates the `{}` a fresh user_settings row carries and anything else
// half-formed that may come out of the sync.
export const normalizeLock = (lock) => ({
  ...DEFAULT_DIARY_LOCK,
  ...(lock && typeof lock === 'object' ? lock : {})
});

export const hasCode = (lock) => {
  const l = normalizeLock(lock);
  return typeof l.codeHash === 'string' && l.codeHash.length > 0 && typeof l.salt === 'string' && l.salt.length > 0;
};

const CODE_RE = new RegExp(`^\\d{${MIN_CODE_LENGTH},${MAX_CODE_LENGTH}}$`);
export const isValidCode = (code) => typeof code === 'string' && CODE_RE.test(code);

// --- hashing -----------------------------------------------------------------

const toHex = (bytes) =>
  Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');

// UTF-8 encode without relying on TextEncoder (absent in the jsdom test env).
const utf8 = (str) => {
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(str);
  const out = [];
  for (const ch of str) {
    const cp = ch.codePointAt(0);
    if (cp < 0x80) out.push(cp);
    else if (cp < 0x800) out.push(0xc0 | (cp >> 6), 0x80 | (cp & 63));
    else if (cp < 0x10000) out.push(0xe0 | (cp >> 12), 0x80 | ((cp >> 6) & 63), 0x80 | (cp & 63));
    else out.push(0xf0 | (cp >> 18), 0x80 | ((cp >> 12) & 63), 0x80 | ((cp >> 6) & 63), 0x80 | (cp & 63));
  }
  return Uint8Array.from(out);
};

// Plain SHA-256 (FIPS 180-4) for environments without crypto.subtle — the
// WebCrypto path is what browsers and the iOS shell actually use; this keeps
// hashing working in the jsdom test runner and on the rare http:// origin.
const K = [
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2
];
const rotr = (x, n) => (x >>> n) | (x << (32 - n));

export const sha256HexFallback = (bytes) => {
  const H = [0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19];
  const len = bytes.length;
  const padded = new Uint8Array(((len + 9 + 63) >> 6) << 6);
  padded.set(bytes);
  padded[len] = 0x80;
  const view = new DataView(padded.buffer);
  const bitLen = len * 8;
  view.setUint32(padded.length - 8, Math.floor(bitLen / 0x100000000));
  view.setUint32(padded.length - 4, bitLen >>> 0);
  const w = new Uint32Array(64);
  for (let off = 0; off < padded.length; off += 64) {
    for (let i = 0; i < 16; i++) w[i] = view.getUint32(off + i * 4);
    for (let i = 16; i < 64; i++) {
      const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
      const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
    }
    let [a, b, c, d, e, f, g, h] = H;
    for (let i = 0; i < 64; i++) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const t1 = (h + S1 + ch + K[i] + w[i]) >>> 0;
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + maj) >>> 0;
      h = g; g = f; f = e; e = (d + t1) >>> 0;
      d = c; c = b; b = a; a = (t1 + t2) >>> 0;
    }
    H[0] = (H[0] + a) >>> 0; H[1] = (H[1] + b) >>> 0; H[2] = (H[2] + c) >>> 0; H[3] = (H[3] + d) >>> 0;
    H[4] = (H[4] + e) >>> 0; H[5] = (H[5] + f) >>> 0; H[6] = (H[6] + g) >>> 0; H[7] = (H[7] + h) >>> 0;
  }
  return H.map((x) => x.toString(16).padStart(8, '0')).join('');
};

// window.crypto in browsers and the iOS shell; absent in the jsdom test env.
const getCrypto = () =>
  typeof window !== 'undefined' && window.crypto ? window.crypto : undefined;

const defaultSubtle = () => {
  const c = getCrypto();
  return c && c.subtle && typeof c.subtle.digest === 'function' ? c.subtle : null;
};

// SHA-256 over `salt:code`, lowercase hex. `subtle` is injectable so tests can
// pass Node's webcrypto (or null to force the fallback).
export const hashCode = async (code, salt, subtle = defaultSubtle()) => {
  const bytes = utf8(`${salt}:${code}`);
  if (subtle) {
    const digest = await subtle.digest('SHA-256', bytes);
    return toHex(new Uint8Array(digest));
  }
  return sha256HexFallback(bytes);
};

export const newSalt = () => {
  const bytes = new Uint8Array(16);
  const c = getCrypto();
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  return toHex(bytes);
};

const constantTimeEqual = (a, b) => {
  if (typeof a !== 'string' || typeof b !== 'string' || a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
};

export const verifyCode = async (code, lock, subtle) => {
  const l = normalizeLock(lock);
  if (!hasCode(l) || typeof code !== 'string' || !code) return false;
  const h = await hashCode(code, l.salt, subtle);
  return constantTimeEqual(h, l.codeHash);
};

// --- biometrics (native only) ------------------------------------------------

const Bridge = Capacitor.isNativePlatform() ? registerPlugin('EuleaWidgetBridge') : null;

// Whether this build can even ask — true in the iOS shell, false on the web.
// Whether the *device* has Face ID enrolled is only known after asking
// (`available` in the result).
export const biometricsSupported = () => !!Bridge;

// → { ok: boolean, available: boolean }. Never throws: an app build whose
// native side predates the method counts as "not available" so the UI falls
// back to the code; any other failure keeps the lock shut (ok:false).
export const authenticateWithBiometrics = async (reason) => {
  if (!Bridge) return { ok: false, available: false };
  try {
    const res = await Bridge.authenticate({ reason });
    return { ok: !!(res && res.ok), available: !res || res.available !== false };
  } catch (err) {
    if (err && err.code === 'UNIMPLEMENTED') return { ok: false, available: false };
    return { ok: false, available: true };
  }
};

// --- session state -----------------------------------------------------------

let unlockedUntil = 0;
let expiryTimer = null;
let listenersInstalled = false;
const listeners = new Set();

const notify = () => {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch (_) {}
  });
};

export const isUnlocked = (now = Date.now()) => unlockedUntil > now;

export const lock = () => {
  if (expiryTimer) {
    clearTimeout(expiryTimer);
    expiryTimer = null;
  }
  const was = unlockedUntil > 0;
  unlockedUntil = 0;
  if (was) notify();
};

const installListeners = () => {
  if (listenersInstalled) return;
  listenersInstalled = true;
  if (typeof document !== 'undefined' && document.addEventListener) {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') lock();
    });
  }
  if (Capacitor.isNativePlatform()) {
    import('@capacitor/app')
      .then(({ App }) =>
        App.addListener('appStateChange', ({ isActive }) => {
          if (!isActive) lock();
        })
      )
      .catch(() => {});
  }
};

export const markUnlocked = (now = Date.now()) => {
  installListeners();
  if (expiryTimer) clearTimeout(expiryTimer);
  unlockedUntil = now + UNLOCK_TTL_MS;
  expiryTimer = setTimeout(lock, UNLOCK_TTL_MS);
  notify();
};

// Components re-render on lock/unlock through this; returns the unsubscribe.
export const subscribeLockState = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};
